import React, { useEffect, useState } from "react";
import api from "@/api/client";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PageHeader, KpiCard, Panel, formatEur } from "@/components/shared/PageUi";
import CrudList from "@/components/shared/CrudList";
import ModuleStaffTab from "@/components/staff/ModuleStaffTab";
import { FolderOpen, ListTodo, CheckCircle2, Wallet, Users } from "lucide-react";

const STATUS = [
  { value: "planned", label: "Suunniteltu" },
  { value: "active", label: "Käynnissä" },
  { value: "on_hold", label: "Tauolla" },
  { value: "completed", label: "Valmis" },
  { value: "cancelled", label: "Peruutettu" },
];
const PRIORITY = [
  { value: "low", label: "Matala" },
  { value: "normal", label: "Normaali" },
  { value: "high", label: "Korkea" },
  { value: "urgent", label: "Kiireellinen" },
];
const TASK_STATUS = [
  { value: "todo", label: "Tehtävälistalla" },
  { value: "in_progress", label: "Käynnissä" },
  { value: "review", label: "Arvioinnissa" },
  { value: "done", label: "Valmis" },
  { value: "cancelled", label: "Peruutettu" },
];

export default function ProjectsDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/projects/stats").then((r) => setStats(r.data)).catch(() => {}).finally(() => setLoading(false)); }, []);

  const projects = {
    endpoint: "/projects/items",
    testidPrefix: "projects",
    emptyMessage: "Ei projekteja",
    defaults: { name: "", status: "planned", priority: "normal", budget: 0, spent: 0, progress_percent: 0 },
    fields: [
      { key: "name", label: "Projektin nimi", type: "text", required: true },
      { key: "customer_name", label: "Asiakas", type: "text" },
      { key: "manager", label: "Vastuuhenkilö", type: "text" },
      { key: "start_date", label: "Alkaa", type: "date" },
      { key: "end_date", label: "Päättyy", type: "date" },
      { key: "status", label: "Tila", type: "select", options: STATUS },
      { key: "priority", label: "Prioriteetti", type: "select", options: PRIORITY },
      { key: "budget", label: "Budjetti €", type: "number" },
      { key: "spent", label: "Käytetty €", type: "number" },
      { key: "progress_percent", label: "Edistyminen %", type: "number" },
      { key: "description", label: "Kuvaus", type: "textarea", span: 2 },
      { key: "notes", label: "Muistiinpanot", type: "textarea", span: 2 },
    ],
    columns: [
      { key: "project_number", label: "Nro" },
      { key: "name", label: "Nimi" },
      { key: "customer_name", label: "Asiakas" },
      { key: "manager", label: "Vastuu" },
      { key: "budget", label: "Budjetti", format: "eur", align: "right" },
      { key: "spent", label: "Käytetty", format: "eur", align: "right" },
      { key: "progress_percent", label: "% valmis", align: "right" },
      { key: "status", label: "Tila", format: "badge" },
    ],
  };

  const tasks = {
    endpoint: "/projects/tasks",
    testidPrefix: "tasks",
    emptyMessage: "Ei tehtäviä",
    defaults: { title: "", status: "todo", priority: "normal", estimated_hours: 0, actual_hours: 0 },
    fields: [
      { key: "title", label: "Otsikko", type: "text", required: true },
      { key: "project_name", label: "Projekti", type: "text" },
      { key: "assigned_to", label: "Vastuuhenkilö", type: "text" },
      { key: "due_date", label: "Eräpäivä", type: "date" },
      { key: "priority", label: "Prioriteetti", type: "select", options: PRIORITY },
      { key: "status", label: "Tila", type: "select", options: TASK_STATUS },
      { key: "estimated_hours", label: "Arvio tunteja", type: "number" },
      { key: "actual_hours", label: "Toteutuneet tunnit", type: "number" },
      { key: "description", label: "Kuvaus", type: "textarea", span: 2 },
    ],
    columns: [
      { key: "title", label: "Tehtävä" },
      { key: "project_name", label: "Projekti" },
      { key: "assigned_to", label: "Vastuu" },
      { key: "due_date", label: "Eräpäivä" },
      { key: "priority", label: "Prio", format: "badge" },
      { key: "estimated_hours", label: "Arvio h", align: "right" },
      { key: "actual_hours", label: "Toteut. h", align: "right" },
      { key: "status", label: "Tila", format: "badge" },
    ],
  };

  return (
    <div className="space-y-6" data-testid="projects-dashboard">
      <PageHeader kicker="Sovellus" title="Projektit" description="Projektit, tehtävät ja budjetit." />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard icon={<FolderOpen className="w-5 h-5" />} label="Aktiiviset projektit" value={stats?.projects_active ?? 0} accent="text-emerald-300" loading={loading} testid="proj-kpi-active" />
        <KpiCard icon={<CheckCircle2 className="w-5 h-5" />} label="Valmistuneet" value={stats?.projects_completed ?? 0} loading={loading} testid="proj-kpi-done" />
        <KpiCard icon={<ListTodo className="w-5 h-5" />} label="Tehtäviä (TODO+käynn.)" value={(stats?.tasks_todo || 0) + (stats?.tasks_in_progress || 0)} accent="text-blue-300" loading={loading} testid="proj-kpi-tasks" />
        <KpiCard icon={<Wallet className="w-5 h-5" />} label="Budjetit yht." value={formatEur(stats?.budget_total)} accent="text-amber-300" loading={loading} testid="proj-kpi-budget" />
      </div>
      <Panel title="Hallinta">
        <Tabs defaultValue="projects" className="w-full">
          <TabsList className="bg-amber-500/10 mb-4">
            <TabsTrigger value="projects" data-testid="proj-tab-projects">Projektit</TabsTrigger>
            <TabsTrigger value="tasks" data-testid="proj-tab-tasks">Tehtävät</TabsTrigger>
            <TabsTrigger value="staff" data-testid="proj-tab-staff"><Users className="w-3.5 h-3.5 mr-1.5" />Henkilökunta</TabsTrigger>
          </TabsList>
          <TabsContent value="projects"><CrudList config={projects} /></TabsContent>
          <TabsContent value="tasks"><CrudList config={tasks} /></TabsContent>
          <TabsContent value="staff"><ModuleStaffTab moduleKey="projects" moduleLabel="Projektit" /></TabsContent>
        </Tabs>
      </Panel>
    </div>
  );
}
